import { useCallback, useMemo, useState } from 'react'
import React from 'react'

import { IDENTITY } from 'constants/commonConstans'

import FieldLayout from '../FieldLayout'
import { renderDescription, renderInfo, renderLabel } from '../renderFieldBase'
import type { IFieldConfig, IFieldChange } from '../types'
import { useConfig } from '../useConfig'
import { validateRules } from '../validateRules'

interface IFieldProps<V> {
  config: IFieldConfig
  value: V
  onChange: (value: IFieldChange<V>) => void
  onBlur?: <E>(event?: React.FocusEvent<E>) => void
  onFocus?: <E>(event?: React.FocusEvent<E>) => void
}

const SingleField = <V,>({
  config: initialConfig,
  value,
  onChange,
  onBlur,
  onFocus,
}: IFieldProps<V>) => {
  const [touched, setTouched] = useState(false)
  const [error, setError] = useState<string | false>()

  const config = useConfig(initialConfig)
  const { getter = IDENTITY, effect = IDENTITY, setter = IDENTITY, ...rest } = config
  const { as: Control, name, hidden, layout, layoutRender, layoutModifiers, props } = rest
  const { disabled, size, defaultValue, readOnly, rules } = rest

  const label = useMemo(() => renderLabel(config), [config])
  const info = useMemo(() => renderInfo(config), [config])
  const description = useMemo(() => renderDescription(config), [config])
  const required = layoutModifiers?.includes('required')

  const handleChange = useCallback(
    async (fieldChange: IFieldChange) => {
      const data = await effect(fieldChange)
      if (Array.isArray(data)) {
        data.forEach((change) => {
          const { value, name, meta } = change
          setTouched(true)
          setError(validateRules(setter(value), rules) ?? false)
          onChange({ name, value: setter(value), meta })
        })
      } else {
        const { value, name, meta } = data
        setTouched(true)
        setError(validateRules(setter(value), rules) ?? false)
        onChange({ name, value: setter(value), meta })
      }
    },
    [effect, onChange, setter, rules],
  )

  if (hidden) {
    return null
  }

  return (
    <FieldLayout
      className={config.className}
      Component={layoutRender}
      description={description}
      error={touched && error}
      info={info}
      label={label}
      layout={layout}
      layoutModifiers={layoutModifiers}
      name={name}
      required={required}
      size={size}
    >
      <Control
        {...props}
        defaultValue={getter(defaultValue)}
        disabled={disabled}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={onFocus}
        readOnly={readOnly}
        size={size}
        value={getter(value)}
      />
    </FieldLayout>
  )
}

export default React.memo(SingleField) as typeof SingleField
